import jsPDF from "jspdf";
import logoSrc from '../assets/img/logo.png';

export class PdfHelper {
  private logoImg: HTMLImageElement;

  constructor(
    public readonly pdf: jsPDF,
    private readonly serialNumber: string,
    private readonly reportTitle: string,
    private readonly reportTimeframe: string,
    private readonly reportCreationInformation: string,
    private readonly pageText: string,
    private readonly accuracyNoteText: string,
    private readonly isMobile?: boolean,
  ) {
    this.logoImg = new Image();
    this.logoImg.src = logoSrc;
  }

  public createReportHeader(): PdfHelper {
    const pageWidth = this.pdf.internal.pageSize.getWidth();

    this.pdf
      // Title
      .setFontSize(20)
      .setFontType('bold')
      .text(this.reportTitle, 16, 16)

      // Time frame
      .setFontSize(12)
      .setFontType('italic')
      .text(this.reportTimeframe, 16, 24)

      // Logo
      .addImage(
        this.logoImg,
        'PNG',
        pageWidth - 70 / 2,
        0,
        37.5 / 2,
        46.875 / 2
      );
    
    this.drawHorizontalLine(16, 30);

    return this;
  };

  public createReportFooter(
    pageNumber?: number,
    pageCount?: number,
  ): PdfHelper {
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const pageHeight = this.pdf.internal.pageSize.getHeight();

    this.drawHorizontalLine(16, pageHeight - 22);

    this.pdf
      // Report creation information and serial number
      .setFontSize(12)
      .setFontType('italic')
      .text(
        this.reportCreationInformation,
        16,
        pageHeight - 16
      )
      .text(
        this.serialNumber,
        pageWidth / 2,
        pageHeight - 16,
        {
          align: 'center',
        }
      )

      // Accuracy note
      .setFontSize(10)
      .text(
        this.accuracyNoteText,
        pageWidth / 2,
        pageHeight - 10,
        {
          align: 'center',
        }
      );

    if (pageNumber !== undefined && pageCount !== undefined) {
      this.writePageNumber(pageNumber, pageCount);
    }

    return this;
  };

  /**
   * drawing the 2 img parameters to the current page
   * @param imgLeft left image for a narrow chart - empty space when undefined
   * @param imgRight right image for a wide chart - empty space when undefined
   * @returns current instance of PdfHelper for chaining
   */
  public createGraphReports(
    imgLeft: HTMLCanvasElement | undefined,
    imgRight: HTMLCanvasElement | undefined | null,
  ): PdfHelper {
    const height = (this.pdf.internal.pageSize.getHeight() - 125) / 2;
    const smallChartWidth = this.isMobile ? 65 : 75;
    const graphX = this.isMobile ? 80 : 90;

    if (imgLeft && imgRight === null) {
      // Nur linkes Bild, aber in voller Breite
      this.pdf.addImage(
        imgLeft,
        'JPEG',
        10,
        height,
        200,
        125,
        undefined,
        'FAST'
      );
    } else {
      if (imgLeft){
        this.pdf.addImage(
          imgLeft,
          'JPEG',
          10,
          height,
          smallChartWidth,
          125,
          undefined,
          'FAST'
        );
      }

      if (imgRight){
        this.pdf.addImage(
          imgRight,
          'JPEG',
          graphX,
          height,
          200,
          125,
          undefined,
          'FAST'
        );
      }
    }

    return this;
  };

  /**
   * when any of the 2 img parameters is set, a new page is created, containing:
   *   - header
   *   - footer
   *   - the provided graphics
   * 
   * when no img parameter is set, no page is created
   * 
   * @param imgLeft left image for a narrow chart - empty space when undefined
   * @param imgRight right image for a wide chart - empty space when undefined
   * @returns current instance of PdfHelper for chaining
   */
  public addGraphReportsPage(
    imgLeft: HTMLCanvasElement | undefined,
    imgRight: HTMLCanvasElement | undefined | null
  ): PdfHelper {
    if (!imgLeft && !imgRight){
      return this;
    }

    this.pdf.addPage();

    return this
      .createReportHeader()
      .createGraphReports(imgLeft, imgRight)
      .createReportFooter();
  }

  /**
   * writing the current page number and absolute page count in the bottom right corner of the current page
   * @param pageNumber current page number
   * @param pageCount absolute page count
   * @returns current instance of PdfHelper for chaining
   */
  public writePageNumber(
    pageNumber: number,
    pageCount: number
  ): PdfHelper {
    this.pdf
      .setFontSize(12)
      .text(
        `${this.pageText} ${pageNumber} / ${pageCount}`,
        this.pdf.internal.pageSize.getWidth() - 32,
        this.pdf.internal.pageSize.getHeight() - 16
      );

    return this;
  }

  /**
   * iterating over every page to write the current page number and absolute page count
   * 
   * WARING: absolute page number is only available after all pages were created
   * 
   * @returns current instance of PdfHelper for chaining
   */
  public writeAllPageNumbers(): PdfHelper {
    const pageCount = this.pdf.internal.getNumberOfPages();

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      this.pdf.setPage(pageNumber);
      this.writePageNumber(pageNumber, pageCount);
    }

    return this;
  }

  public drawHorizontalLine(
    marginX: number,
    y: number,
    lineWidth = 0.25
  ): PdfHelper {
    this.pdf
      .setLineWidth(lineWidth)
      .line(
        marginX,
        y,
        this.pdf.internal.pageSize.getWidth() - marginX,
        y
      );

    return this;
  }
}